import React, { useState, useEffect, useCallback } from "react";
import { Building2, Search, X, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import baseapi from "../../services/baseapi";
import { type LocationIds } from "../../components/LocationSelectorSection";

interface SiteListSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    locationIds: LocationIds;
    level: any;
}

export const SiteListSidebar: React.FC<SiteListSidebarProps> = ({
    isOpen,
    onClose,
    locationIds,
    level
}) => {
    const navigate = useNavigate();
    const [siteListLoading, setSiteListLoading] = useState(false);
    const [siteList, setSiteList] = useState<any[]>([]);
    const [siteListSearch, setSiteListSearch] = useState("");
    const [siteListPage, setSiteListPage] = useState(1);
    const [siteListLimit] = useState(10);
    const [siteListTotal, setSiteListTotal] = useState(0);

    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

    if (isOpen !== prevIsOpen) {
        setPrevIsOpen(isOpen);
        if (isOpen) {
            setSiteListPage(1);
            setSiteListSearch("");
        }
    }

    const fetchSiteList = useCallback(async (page = 1, search = "") => {
        setSiteListLoading(true);
        try {
            const queryObj: any = {};
            if (locationIds.regionId) queryObj.region_id = locationIds.regionId;
            if (locationIds.stateId) queryObj.state_id = locationIds.stateId;
            if (locationIds.districtId) queryObj.district_id = locationIds.districtId;
            if (locationIds.townId) queryObj.town_id = locationIds.townId;
            if (locationIds.siteId && locationIds.siteId !== "all") {
                queryObj.id = locationIds.siteId;
            } else if (level?.id && level?.name !== 'global') {
                queryObj.id = Number(level.id);
            }

            if (search.trim()) {
                queryObj.$or = [
                    { name: { "$ilike": `%${search}%` } },
                    { display_name: { "$ilike": `%${search}%` } }
                ];
            }

            const response = await baseapi.get("/clients", {
                params: {
                    query: JSON.stringify(queryObj),
                    skip: (page - 1) * siteListLimit,
                    limit: siteListLimit,
                    sort_by: "created",
                    sort_order: "desc"
                }
            });

            if (response.data?.status) {
                setSiteList(response.data.data || []);
                setSiteListTotal(response.data.total || 0);
            } else {
                setSiteList([]);
                setSiteListTotal(0);
            }
        } catch (error) {
            console.error("Failed to fetch site list:", error);
            setSiteList([]);
            setSiteListTotal(0);
        } finally {
            setSiteListLoading(false);
        }
    }, [locationIds, level, siteListLimit]);

    useEffect(() => {
        if (isOpen) {
            fetchSiteList(siteListPage, siteListSearch);
        }
    }, [isOpen, siteListPage, siteListSearch, fetchSiteList]);

    const handleSiteClick = (clientId: number) => {
        onClose();
        navigate(`/client-details?client_id=${clientId}&path=dashboard`);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]"
                    />

                    {/* Side panel */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-[500px] bg-white border-l border-slate-200 z-[101] flex flex-col shadow-2xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
                                    <Building2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest leading-none">
                                        Sites List
                                    </h2>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-1">
                                        Overview of registered site facilities
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 min-h-0 flex flex-col p-4">
                            {/* Search Input */}
                            <div className="relative mb-4 shrink-0">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search by site name or display name..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-8 py-2 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568]/15 focus:border-[#422568] transition-all"
                                    value={siteListSearch}
                                    onChange={(e) => {
                                        setSiteListSearch(e.target.value);
                                        setSiteListPage(1);
                                    }}
                                />
                                {siteListSearch && (
                                    <button
                                        onClick={() => {
                                            setSiteListSearch("");
                                            setSiteListPage(1);
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full hover:bg-slate-200"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Sites List */}
                            <div className="flex-1 overflow-y-auto custom-scroll pr-1 space-y-2 mb-2">
                                {siteListLoading ? (
                                    <div className="h-full flex flex-col items-center justify-center py-20">
                                        <div className="w-8 h-8 border-4 border-[#422568]/10 border-t-[#422568] rounded-full animate-spin mb-3" />
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">
                                            Loading Sites...
                                        </span>
                                    </div>
                                ) : siteList.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center py-20 text-slate-400 opacity-60">
                                        <Building2 className="w-10 h-10 mb-2.5" />
                                        <p className="text-[10px] font-black uppercase tracking-wider">No Sites Found</p>
                                    </div>
                                ) : (
                                    siteList.map((site: any) => (
                                        <div
                                            key={site.id}
                                            onClick={() => handleSiteClick(site.id)}
                                            className="bg-white border border-slate-200 hover:border-[#422568]/30 rounded-xl p-3 flex flex-col gap-2 transition-all shadow-sm hover:shadow group cursor-pointer"
                                        >
                                            {/* Top row: Name & Display Name */}
                                            <div className="flex justify-between items-start gap-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1 bg-[#422568]/5 text-[#422568] rounded-lg">
                                                        <Building2 className="w-3.5 h-3.5" />
                                                    </div>
                                                    <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-tight group-hover:text-[#422568] transition-colors truncate max-w-[240px]">
                                                        {site.name || "Unnamed Site"}
                                                    </h4>
                                                </div>
                                                {site.display_name && (
                                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[8px] font-black uppercase tracking-wider text-slate-500 border border-slate-200 shadow-sm shrink-0">
                                                        {site.display_name}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Location Details */}
                                            {(site.region_name || site.state_name || site.district_name || site.town_name) && (
                                                <div className="flex items-start gap-1.5 text-[9px] text-slate-500 pt-1.5 border-t border-slate-100/60">
                                                    <MapPin className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                                                    <span className="leading-tight truncate">
                                                        {[
                                                            site.region_name,
                                                            site.state_name,
                                                            site.district_name,
                                                            site.town_name
                                                        ].filter(Boolean).join(", ")}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Description if present */}
                                            {site.description && (
                                                <p className="text-[9px] text-slate-400 font-medium italic line-clamp-2 pl-5">
                                                    {site.description}
                                                </p>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Pagination Controls */}
                            {siteListTotal > siteListLimit && (
                                <div className="shrink-0 pt-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-b-xl px-2 py-1.5">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        Page {siteListPage} of {Math.ceil(siteListTotal / siteListLimit)}
                                    </span>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => setSiteListPage((p) => Math.max(1, p - 1))}
                                            disabled={siteListPage === 1}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Prev
                                        </button>
                                        <button
                                            onClick={() =>
                                                setSiteListPage((p) =>
                                                    Math.min(Math.ceil(siteListTotal / siteListLimit), p + 1)
                                                )
                                            }
                                            disabled={siteListPage >= Math.ceil(siteListTotal / siteListLimit)}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};
